<script setup>
import { Head } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import BrandHero from '@/Components/Public/BrandHero.vue'
import LuxButton from '@/Components/Public/LuxButton.vue'
import ProductCard from '@/Components/Public/ProductCard.vue'

// One place, told as a day: the facts a guest asks first, what is there,
// the boats that sail there, the experiences there, a day there, and the
// questions people ask.
defineOptions({ layout: PublicLayout })

const { t } = useI18n()
defineProps({
  slug:       { type: String, required: true },
  content:    { type: Object, required: true },  // title, lede, facts[{label,value}], highlights[], day[{time,text}], tips[], faq[{q,a}]
  charters:   { type: Array,  default: () => [] },
  tours:      { type: Array,  default: () => [] },
  siblings:   { type: Array,  default: () => [] },
  paths:      { type: Object, required: true }   // charters, tours, destinations, plan
})
</script>

<template>
  <Head>
    <title>{{ content.title }} · {{ t('public.charter_home.head_title') }}</title>
    <meta name="description" :content="content.lede">
  </Head>

  <BrandHero kind="charter" :eyebrow="t('public.destinations.eyebrow')" :title="content.title">
    <template #top>
      <a :href="paths.destinations" class="bhero-back">← {{ t('public.destinations.all') }}</a>
    </template>
    <p class="bhero-lede">{{ content.lede }}</p>
    <div class="bhero-ctas">
      <LuxButton :href="paths.plan" variant="solid" tone="dark" glyph="arrow">{{ t('public.destinations.plan_cta') }}</LuxButton>
      <LuxButton :href="paths.charters" variant="line" tone="dark">{{ t('public.charter_home.hero.cta_fleet') }}</LuxButton>
    </div>
  </BrandHero>

  <section class="body">
    <div class="inner">
      <dl class="facts" :aria-label="t('public.destinations.facts_title')">
        <div v-for="(f, i) in content.facts" :key="i" class="fact">
          <dt>{{ f.label }}</dt>
          <dd>{{ f.value }}</dd>
        </div>
      </dl>

      <div class="two">
        <section class="block">
          <p class="eyebrow">{{ t('public.destinations.highlights_title') }}</p>
          <ul class="ticks">
            <li v-for="(h, i) in content.highlights" :key="i">{{ h }}</li>
          </ul>
        </section>
        <section class="block block--card">
          <h2 class="h2 h2--small">{{ t('public.destinations.day_title') }}</h2>
          <ol class="day">
            <li v-for="(s, i) in content.day" :key="i"><span class="day__time mono">{{ s.time }}</span><span class="day__text">{{ s.text }}</span></li>
          </ol>
        </section>
      </div>

      <section class="block">
        <div class="block__head">
          <h2 class="h2">{{ t('public.destinations.boats_title') }}</h2>
          <a :href="paths.charters" class="more">{{ t('public.charter_home.fleet.all') }} →</a>
        </div>
        <ul v-if="charters.length" class="grid">
          <li v-for="c in charters" :key="c.id"><ProductCard kind="charter" :item="c" :href="`${paths.charters}/${c.id}`" /></li>
        </ul>
        <p v-else class="empty">{{ t('public.destinations.boats_empty') }} <a :href="paths.plan" class="more">{{ t('public.destinations.plan_cta') }} →</a></p>
      </section>

      <section v-if="tours.length" class="block">
        <div class="block__head">
          <h2 class="h2">{{ t('public.destinations.experiences_title') }}</h2>
          <a :href="paths.tours" class="more">{{ t('public.charter_home.experiences.all') }} →</a>
        </div>
        <ul class="grid">
          <li v-for="x in tours" :key="x.id"><ProductCard kind="tour" :item="x" :href="`${paths.tours}/${x.id}`" /></li>
        </ul>
      </section>

      <div class="two">
        <section class="block block--card">
          <h2 class="h2 h2--small">{{ t('public.destinations.tips_title') }}</h2>
          <ul class="tips">
            <li v-for="(tip, i) in content.tips" :key="i">{{ tip }}</li>
          </ul>
        </section>
        <section class="block block--card">
          <h2 class="h2 h2--small">{{ t('public.destinations.faq_title') }}</h2>
          <details v-for="(f, i) in content.faq" :key="i" class="faq">
            <summary class="faq__q">{{ f.q }}</summary>
            <p class="faq__a">{{ f.a }}</p>
          </details>
        </section>
      </div>

      <nav class="siblings" :aria-label="t('public.destinations.others_title')">
        <span class="siblings__label">{{ t('public.destinations.others_title') }}</span>
        <a v-for="s in siblings" :key="s.slug" :href="`${paths.destinations}/${s.slug}`" class="chip">{{ s.title }}</a>
      </nav>

      <div class="plan-band">
        <p>{{ t('public.destinations.plan_band') }}</p>
        <LuxButton :href="paths.plan" variant="solid" tone="light" glyph="arrow">{{ t('public.destinations.plan_cta') }}</LuxButton>
      </div>
    </div>
  </section>
</template>

<style scoped>
.bhero-back { display: inline-block; margin-bottom: 1rem; color: inherit; opacity: 0.85; text-decoration: none; font-size: 0.95rem; }
.bhero-lede { margin: 0.9rem 0 0; font-size: clamp(1.05rem, 2vw, 1.25rem); line-height: 1.5; max-width: 56ch; opacity: 0.94; }
.bhero-ctas { display: flex; flex-wrap: wrap; gap: 0.6rem; margin-top: 1.5rem; }

.body { background: var(--bg); }
.inner { max-width: 80rem; margin: 0 auto; padding: clamp(2rem, 4vw, 3rem) clamp(1.25rem, 4vw, 3rem) 4rem; display: grid; gap: clamp(2.5rem, 5vw, 4rem); }
.eyebrow { margin: 0 0 0.75rem; font-size: 0.75rem; letter-spacing: var(--eyebrow-tracking); text-transform: uppercase; font-weight: 700; color: var(--accent); }
.h2 { font-family: var(--font-display, var(--display)); font-weight: 500; font-size: clamp(1.8rem, 3.2vw, 2.6rem); line-height: 1.05; margin: 0; color: var(--ink); text-wrap: balance; }
.h2--small { font-size: 1.7rem; margin-bottom: 1rem; }
.more { font-size: 0.9rem; font-weight: 700; color: var(--azure, var(--second)); text-decoration: none; white-space: nowrap; }
.more:hover { text-decoration: underline; }
.mono { font-family: var(--font-mono, var(--mono)); }

/* The facts strip: four short answers before anything else, like the boat page's stat strip. */
.facts { margin: 0; display: grid; grid-template-columns: repeat(auto-fit, minmax(13rem, 1fr)); gap: 1px; background: var(--line); border: 1px solid var(--line); border-radius: 1.25rem; overflow: hidden; }
.fact { background: var(--surface); padding: 1.1rem 1.25rem; display: grid; gap: 0.3rem; align-content: start; }
.fact dt { font-size: 0.72rem; letter-spacing: 0.1em; text-transform: uppercase; color: var(--muted); font-weight: 700; }
.fact dd { margin: 0; color: var(--ink); line-height: 1.45; font-size: 0.95rem; }

.two { display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; align-items: start; }
.block__head { display: flex; justify-content: space-between; align-items: end; gap: 1rem; margin-bottom: 1.25rem; }
.block--card { background: var(--surface); border: 1px solid var(--line); border-radius: 1.25rem; padding: 1.5rem; }
.grid { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(auto-fill, minmax(17rem, 1fr)); gap: 1.25rem; }
.empty { color: var(--inkSoft); margin: 0; display: flex; flex-wrap: wrap; gap: 0.6rem; align-items: center; }

.ticks { list-style: none; margin: 0; padding: 0; display: grid; gap: 0.7rem; }
.ticks li { position: relative; padding-left: 1.8rem; font-size: 1.08rem; line-height: 1.5; color: var(--ink); }
.ticks li::before { content: "✓"; position: absolute; left: 0; top: 0.05rem; color: var(--ok); font-weight: 700; }
.day { list-style: none; margin: 0; padding: 0; display: grid; gap: 0.75rem; }
.day li { display: grid; grid-template-columns: 4.5rem 1fr; gap: 0.75rem; border-top: 1px solid var(--line); padding-top: 0.75rem; }
.day li:first-child { border-top: 0; padding-top: 0; }
.day__time { font-size: 0.8rem; letter-spacing: 0.04em; color: var(--accent); padding-top: 0.2rem; }
.day__text { color: var(--inkSoft); line-height: 1.5; }
.tips { list-style: none; margin: 0; padding: 0; display: grid; gap: 0.7rem; }
.tips li { position: relative; padding-left: 1.4rem; color: var(--inkSoft); line-height: 1.5; }
.tips li::before { content: "·"; position: absolute; left: 0.2rem; top: -0.1rem; font-size: 1.6rem; line-height: 1; color: var(--accent); }
.faq { border-top: 1px solid var(--line); padding: 0.75rem 0; }
.faq:first-of-type { border-top: 0; padding-top: 0; }
.faq__q { cursor: pointer; font-weight: 600; color: var(--ink); }
.faq__a { margin: 0.5rem 0 0; color: var(--inkSoft); line-height: 1.55; }
.siblings { display: flex; flex-wrap: wrap; gap: 0.5rem; align-items: center; }
.siblings__label { font-size: 0.72rem; letter-spacing: 0.1em; text-transform: uppercase; color: var(--muted); font-weight: 700; margin-right: 0.25rem; }
.chip { display: inline-flex; padding: 0.55rem 1rem; border-radius: 999px; background: var(--surface); border: 1px solid var(--line); color: var(--ink); font-size: 0.9rem; font-weight: 600; text-decoration: none; }
.chip:hover { border-color: color-mix(in oklch, var(--gold) 70%, var(--line)); }
.plan-band { padding: 1.5rem 1.75rem; border-radius: 1.25rem; background: linear-gradient(160deg, var(--sand, var(--surfaceAlt)) 0%, var(--accentSoft) 100%); display: flex; flex-wrap: wrap; gap: 1rem; align-items: center; justify-content: space-between; }
.plan-band p { margin: 0; color: var(--ink); font-family: var(--font-display, var(--display)); font-size: 1.35rem; line-height: 1.2; max-width: 40ch; }

@media (max-width: 56rem) { .two { grid-template-columns: 1fr; } }
</style>
